import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  InputGroup,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import reactSelectCustomStyles from "../../common/plugins/reactSelectStyles";
import EnterpriseService from "../../common/services/enterprise-service";
import truckyService from "../../common/services/trucky-service";
import { EnterpriseContext } from "../enterprise/EnterpriseContext";
import { confirmWrapper } from "../ui/askConfirm";
import Select from "react-select";
import LoadingOverlay from "react-loading-overlay";
import { toast, ToastContainer } from "react-toastify";
import Tips from "../Tips";
import tipsList from "../../common/constants/tipsList";
import BoxDynamicAd from "../../ads/BoxDynamicAd";
import {
  HOSTING_2GB_PLAN,
  LEGACY_HOSTING_PLAN,
} from "../../common/constants/hosting";

const MySubscriptions = ({ container }) => {
  const { globalContext } = useContext(EnterpriseContext);

  const [subscriptions, setSubscriptions] = useState([]);
  const [userSubscriptions, setUserSubscriptions] = useState([]);
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(
    globalContext.member.country,
  );
  const es = new EnterpriseService();
  const [quantity, setQuantity] = useState(1);
  const [prices, setPrices] = useState([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [accountManagementUri, setAccountManagementUri] = useState(null);
  const [showBillingDetailModal, toggleBillingDetailModal] = useState(false);
  const [subscriptionUpgradePreview, setSubscriptionUpgradePreview] =
    useState(null);
  const [selectedSubscriptionName, setSelectedSubscriptionName] = useState("");
  const [billingData, setBillingData] = useState({
    firstName: "",
    lastName: "",
  });

  useEffect(() => {
    truckyService.setDocumentTitle("My Subscriptions");

    getCountries();
    getAvailableSubscriptions();
  }, []);

  useEffect(() => {}, [quantity]);

  const getUserCountry = async () => {
    try {
      const result = await fetch("https://api.country.is/");
      const json = await result.json();
      setSelectedCountry(json.country);
    } catch (error) {
      setSelectedCountry("US");
    }
  };

  const getCountries = async () => {
    const result = await es.getCountries();
    setCountries(result);
  };

  const getAvailableSubscriptions = async () => {
    setLoading(true);

    await getUserSubscriptions();

    await getUserCountry();

    const result = await es.getAvailableSubscriptions();
    setSubscriptions(
      result.filter(
        (subscription) =>
          subscription.key == LEGACY_HOSTING_PLAN ||
          subscription.key == HOSTING_2GB_PLAN,
      ),
    );

    setLoading(false);
  };

  const getUserSubscriptions = async (loader = false) => {
    if (loader) {
      setLoading(true);
    }
    const result = await es.getUserSubscriptions(true);
    setUserSubscriptions(result.subscriptions);
    if (loader) {
      setLoading(false);
    }
    if (
      result.accountManagementURI != null &&
      result.accountManagementURI != ""
    ) {
      setAccountManagementUri(result.accountManagementURI);
    }
  };

  const createAccount = async () => {
    setLoading(true);
    const result = await es.createSubscriptionServiceAccount({
      ...billingData,
      country: selectedCountry,
    });

    if (result.success) {
      toggleBillingDetailModal(false);
      getSubscriptionLink(selectedSubscriptionName);
    }
  };

  const getSubscriptionLink = async (name) => {
    setLoading(true);

    const result = await es.getSubscribeLink(
      name,
      quantity,
      selectedCountry,
      container.dataService.data.enablePremiumFeature(),
    );

    if (result.success) {
      if (window.overwolf) {
        truckyService.openUrlInDefaultBrowser(result.data.payLink);
      } else {
        window.open(result.data.payLink);
      }
    } else {
      setSelectedSubscriptionName(name);
      toggleBillingDetailModal(true);
    }

    setLoading(false);
  };

  const cancelSubscription = async (name) => {
    if (
      await confirmWrapper(
        container.localeManager.strings.cancelSubscriptionConfirmation,
        { container },
      )
    ) {
      setLoading(true);
      const result = await es.cancelSubscription(name);
      if (result.success) {
        toast.success(container.localeManager.strings.subscriptionCanceled);
      } else {
        toast.error(result.message);
      }
      await getUserSubscriptions();
      setLoading(false);
    }
  };

  const resumeSubscription = async (name) => {
    setLoading(true);
    const result = await es.resumeSubscription(name);
    toast.success(container.localeManager.strings.subscriptionResumed);
    await getUserSubscriptions();
    setLoading(false);
  };

  const previewSubscriptionUpgrade = async (name, quantity) => {
    setLoading(true);
    const result = await es.previewSubscriptionUpgrade(
      name,
      quantity,
      LEGACY_HOSTING_PLAN,
    );
    setLoading(false);

    if (result.success && result.estimate) {
      setSubscriptionUpgradePreview({
        estimate: result.estimate,
        name,
        quantity,
      });
    } else {
      toast.error(result.message || "Unable to calculate the upgrade price");
    }
  };

  const upgradeSubscription = async (name, quantity) => {
    setSubscriptionUpgradePreview(null);
    setLoading(true);
    const result = await es.swapSubscriptionProduct(
      name,
      quantity,
      LEGACY_HOSTING_PLAN,
    );
    if (result.success) {
      toast.success("Subscription Upgraded");
    } else {
      toast.error("Failed to upgrade subscription");
    }
    await getUserSubscriptions();
    setLoading(false);
  };

  const isSubscribed = (name) => {
    return userSubscriptions.find(
      (m) => m.plan == name && m.state != "canceled",
    );
  };

  const getSubscription = (name) => {
    return userSubscriptions.find((m) => m.plan == name);
  };

  const getSubscriptionStatus = (name) => {
    let status = "";
    let sub = getSubscription(name);

    if (sub) status = sub.state;

    return status;
  };

  const legacyHostingSubscription = userSubscriptions.find(
    (subscription) =>
      subscription.plan == LEGACY_HOSTING_PLAN &&
      subscription.state != "deactivated",
  );
  const activeHosting2GbSubscription = userSubscriptions.find(
    (subscription) =>
      subscription.plan == HOSTING_2GB_PLAN && subscription.state == "active",
  );
  const displayedHostingPlan = activeHosting2GbSubscription
    ? HOSTING_2GB_PLAN
    : legacyHostingSubscription
      ? LEGACY_HOSTING_PLAN
      : HOSTING_2GB_PLAN;
  const hosting2GbPlan = subscriptions.find(
    (subscription) => subscription.key == HOSTING_2GB_PLAN,
  );

  const getPricing = (product, productQuantity = quantity) => {
    let pricing;

    if (selectedCountry == null) pricing = product.pricing["US"];
    else {
      if (product.pricing[selectedCountry])
        pricing = product.pricing[selectedCountry];
      else pricing = product.pricing["US"];
    }

    const price = (pricing.price * productQuantity).toFixed(2);

    return `${price} ${pricing.currency}`;
  };

  return (
    <LoadingOverlay
      active={loading}
      spinner={
        <Spinner
          color="primary"
          style={{ width: "4rem", height: "4rem" }}
        ></Spinner>
      }
    >
      <Row className="gx-0">
        <Col>
          <Card>
            <CardBody>
              {window.overwolf && (
                <div class="mb-3 d-flex justify-content-between">
                  <Button
                    size="sm"
                    onClick={() => navigate(-1)}
                    color="secondary"
                  >
                    <FontAwesomeIcon icon="chevron-left"></FontAwesomeIcon>
                    {container.localeManager.strings.back}
                  </Button>
                </div>
              )}
              <h5 className="d-inline-block w-100">
                {container.localeManager.strings.subscriptions}
              </h5>
              <FormGroup row className="my-3">
                <Col sm={4}>
                  <Select
                    onChange={(val) => setSelectedCountry(val.code)}
                    value={countries.find((m) => m.code == selectedCountry)}
                    options={countries}
                    styles={reactSelectCustomStyles}
                    getOptionLabel={(m) => m.name}
                    getOptionValue={(m) => m.code}
                  ></Select>
                </Col>
                <Col className="text-end mt-3 mt-md-0">
                  {accountManagementUri != null && (
                    <Button
                      className="me-3"
                      size="sm"
                      color="primary"
                      onClick={() =>
                        truckyService.openUrlInDefaultBrowser(
                          accountManagementUri,
                        )
                      }
                    >
                      <FontAwesomeIcon icon="cog"></FontAwesomeIcon>Manage your
                      Subscription and Payment Informations
                    </Button>
                  )}
                  <Button
                    size="sm"
                    color="primary"
                    onClick={() => getUserSubscriptions(true)}
                  >
                    <FontAwesomeIcon icon="refresh"></FontAwesomeIcon>
                    {container.localeManager.strings.reload}
                  </Button>
                </Col>
              </FormGroup>
              {legacyHostingSubscription && !activeHosting2GbSubscription && (
                <div className="notice bg-light-warning rounded border-warning border border-dashed p-6 mb-3">
                  <div className="fw-bold mb-1">
                    <strong>Attention:</strong> Subscription Upgrade Required
                  </div>
                  <div>
                    ETS2 and ATS Update 1.61 requires additional hardware
                    resources. Upgrade your Gen 1 Server subscription to Gen 2
                    Server to continue using the service.
                    <br />
                    Otherwise you can continue using Gen 1 Server, but the
                    server won't work for 1.61, you can continue to use it for
                    previous versions of the game.
                  </div>
                </div>
              )}
              {subscriptions
                .filter(
                  (subscription) => subscription.key == displayedHostingPlan,
                )
                .map((m) => {
                  return (
                    <Card key={m.key} className="bg-light">
                      <CardBody>
                        {getSubscriptionStatus(m.key) == "active" && (
                          <div className="notice bg-light-success rounded border-success border border-dashed p-6 mb-3 d-flex justify-content-between align-items-center">
                            <div>
                              <div>
                                <FontAwesomeIcon icon="check-circle"></FontAwesomeIcon>
                                {container.localeManager.formatString(
                                  container.localeManager.strings.subscribedOn,
                                  container.localeManager
                                    .moment(getSubscription(m.key).created_at)
                                    .format("l"),
                                )}
                              </div>
                              {getSubscription(m.key).fastspring_id && (
                                <div className="fs-7 mt-1">
                                  Your Subscription ID: {getSubscription(m.key).fastspring_id}
                                </div>
                              )}
                            </div>
                            <div className="d-flex flex-wrap gap-2">
                              <Button
                                size="sm"
                                color="success"
                                onClick={() => navigate("/hosting")}
                              >
                                <FontAwesomeIcon icon="server" />
                                Your Servers
                              </Button>
                              <Button
                                size="sm"
                                color="danger"
                                onClick={() => cancelSubscription(m.key)}
                              >
                                {container.localeManager.strings.cancel}
                              </Button>
                            </div>
                          </div>
                        )}
                        {getSubscriptionStatus(m.key) == "canceled" && (
                          <div className="notice bg-light-warning rounded border-warning border border-dashed p-6 mb-3 d-flex justify-content-between align-items-center">
                            <div>
                              <div>
                                <FontAwesomeIcon icon="check-circle"></FontAwesomeIcon>
                                {container.localeManager.formatString(
                                  container.localeManager.strings
                                    .subscribedCanceledValidUntil,
                                  container.localeManager
                                    .moment(getSubscription(m.key).swap_at)
                                    .format("l"),
                                )}
                              </div>
                              {getSubscription(m.key).fastspring_id && (
                                <div className="fs-7 mt-1">
                                  Your Subscription ID: {getSubscription(m.key).fastspring_id}
                                </div>
                              )}
                            </div>
                            <div className="d-flex flex-wrap gap-2">
                              <Button
                                size="sm"
                                color="primary"
                                onClick={() => navigate("/hosting")}
                              >
                                <FontAwesomeIcon icon="server" />
                                Your Servers
                              </Button>
                              <Button
                                size="sm"
                                color="success"
                                onClick={() => resumeSubscription(m.key)}
                              >
                                {container.localeManager.strings.resume}
                              </Button>
                            </div>
                          </div>
                        )}
                        <div className="d-flex justify-content-between align-items-center flex-wrap flex-md-nowrap">
                          <div className="flex-grow-1">
                            <div className="fs-2">{m.name}</div>
                            <Row className="g-3 mt-1">
                              <Col xs={12} sm={6} xl={3}>
                                <FontAwesomeIcon
                                  icon="check-circle"
                                  className="me-2 text-success"
                                />{" "}
                                <b>Up to 128 player slots!</b>
                              </Col>
                              <Col xs={12} sm={6} xl={3}>
                                <FontAwesomeIcon
                                  icon="check-circle"
                                  className="me-2 text-success"
                                />{" "}
                                Visible in Sessions Browser
                              </Col>
                              <Col xs={12} sm={6} xl={3}>
                                <FontAwesomeIcon
                                  icon="check-circle"
                                  className="me-2 text-success"
                                />{" "}
                                Multiple regions near you
                              </Col>
                              <Col xs={12} sm={6} xl={3}>
                                <FontAwesomeIcon
                                  icon="check-circle"
                                  className="me-2 text-success"
                                />{" "}
                                Use your mods
                              </Col>
                              <Col xs={12} sm={6} xl={3}>
                                <FontAwesomeIcon
                                  icon="check-circle"
                                  className="me-2 text-success"
                                />{" "}
                                Public IP
                              </Col>
                              <Col xs={12} sm={6} xl={3}>
                                <FontAwesomeIcon
                                  icon="check-circle"
                                  className="me-2 text-success"
                                />{" "}
                                API to get status and logs
                              </Col>
                              <Col xs={12} sm={6} xl={3}>
                                <FontAwesomeIcon
                                  icon="check-circle"
                                  className="me-2 text-success"
                                />{" "}
                                Discord Webhook
                              </Col>
                            </Row>
                            <div className="border-top mt-4 pt-3">
                              <div className="fw-bold mb-2">Discounts</div>
                              <div className="d-flex flex-wrap gap-3 align-items-center">
                                <div className="text-premium fw-bold">
                                  <FontAwesomeIcon
                                    icon="star"
                                    className="me-2"
                                  />
                                  Angel Patrons get 10% off at checkout
                                </div>
                                <div>
                                  <FontAwesomeIcon
                                    icon="server"
                                    className="me-2 text-success"
                                  />
                                  5% off for 3 servers
                                </div>
                                <div>
                                  <FontAwesomeIcon
                                    icon="server"
                                    className="me-2 text-success"
                                  />
                                  8% off for 4+ servers
                                </div>
                              </div>
                              <div className="text-muted fs-7 mt-2">
                                Discounts cannot be combined. The best eligible
                                discount is applied automatically.
                              </div>
                            </div>
                          </div>
                          {legacyHostingSubscription &&
                            m.key == LEGACY_HOSTING_PLAN &&
                            hosting2GbPlan && (
                              <div
                                className="border rounded p-3 ms-md-3 mt-3 mt-md-0"
                                style={{ minWidth: "260px" }}
                              >
                                <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-2">
                                  <span className="text-white">
                                    {container.localeManager.strings
                                      .currentQuantity || "Current quantity"}
                                  </span>
                                  <span className="fs-5 fw-bold text-white">
                                    × {legacyHostingSubscription.quantity}
                                  </span>
                                </div>
                                <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                                  <div>
                                    <div className="text-white mb-1">
                                      {container.localeManager.strings
                                        .currentSubscriptionCost ||
                                        "Current subscription cost"}
                                    </div>
                                    <div className="text-white text-decoration-line-through">
                                      {getPricing(
                                        m,
                                        legacyHostingSubscription.quantity,
                                      )}
                                    </div>
                                  </div>
                                  <FontAwesomeIcon
                                    icon="arrow-right"
                                    className="text-white"
                                  ></FontAwesomeIcon>
                                  <div className="text-end">
                                    <div className="text-white mb-1">
                                      {container.localeManager.strings
                                        .newSubscriptionCost ||
                                        "New subscription cost"}
                                    </div>
                                    <div className="fs-4 fw-bold text-success">
                                      {getPricing(
                                        hosting2GbPlan,
                                        legacyHostingSubscription.quantity,
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="d-flex justify-content-between bg-warning text-black rounded p-2 mb-2">
                                  <span>
                                    {container.localeManager.strings
                                      .pricePerServer || "Price per server"}
                                  </span>
                                  <b>{getPricing(hosting2GbPlan, 1)}</b>
                                </div>
                                <Button
                                  block
                                  size="sm"
                                  color="success"
                                  onClick={() =>
                                    previewSubscriptionUpgrade(
                                      HOSTING_2GB_PLAN,
                                      legacyHostingSubscription.quantity,
                                    )
                                  }
                                >
                                  <FontAwesomeIcon icon="arrow-up" />
                                  {container.localeManager.strings.upgrade}
                                </Button>
                              </div>
                            )}
                          {getSubscriptionStatus(m.key) == "" && (
                            <div className="d-flex align-items-center flex-column align-content-center">
                              <div className="mb-3 text-center">
                                <span className="fs-1 text-premium fw-bolder">
                                  {getPricing(
                                    m,
                                    legacyHostingSubscription?.quantity,
                                  )}
                                </span>
                                <span className="fs-5">
                                  {" "}
                                  /{" "}
                                  {container.localeManager.strings[m.interval]}
                                </span>
                                <div>
                                  {container.localeManager.strings.vatIncluded}
                                </div>
                              </div>
                              <div className="mt-3 mb-1 text-center">
                                {container.localeManager.strings.howManyServers}
                              </div>
                              <div className="mb-3">
                                <InputGroup>
                                  <Button
                                    color="primary"
                                    onClick={() =>
                                      quantity > 1
                                        ? setQuantity(quantity - 1)
                                        : null
                                    }
                                  >
                                    <FontAwesomeIcon
                                      className="me-0"
                                      icon="minus"
                                    ></FontAwesomeIcon>
                                  </Button>
                                  <Input
                                    className="text-center"
                                    value={quantity}
                                  ></Input>
                                  <Button
                                    color="primary"
                                    onClick={() => setQuantity(quantity + 1)}
                                  >
                                    <FontAwesomeIcon
                                      className="me-0"
                                      icon="plus"
                                    ></FontAwesomeIcon>
                                  </Button>
                                </InputGroup>
                              </div>
                              <Button
                                color="success"
                                onClick={() => getSubscriptionLink(m.key)}
                              >
                                {container.localeManager.strings.subscribe}
                              </Button>
                            </div>
                          )}
                        </div>
                      </CardBody>
                    </Card>
                  );
                })}
            </CardBody>
          </Card>
        </Col>
        <aside className="ad-column-placeholder">
          <Tips
            tipKey={tipsList.KEYS.SUBSCRIPTIONS}
            localeManager={container.localeManager}
          ></Tips>
        </aside>
      </Row>
      <Modal
        centered
        scrollable
        isOpen={subscriptionUpgradePreview != null}
        toggle={() => setSubscriptionUpgradePreview(null)}
      >
        <ModalHeader
          className="py-3"
          toggle={() => setSubscriptionUpgradePreview(null)}
        >
          <FontAwesomeIcon icon="arrow-up" className="me-2 text-success" />
          Subscription upgrade summary
        </ModalHeader>
        {subscriptionUpgradePreview && (
          <>
            <ModalBody className="p-3">
              <div className="notice bg-light-success rounded border-success border border-dashed p-3 mb-3 fs-7">
                Includes applicable Patron and volume discounts, prorated
                charges and taxes.
              </div>

              <div className="border rounded p-3 mb-3 fs-7">
                <Row className="g-2 fw-bold border-bottom pb-2 mb-2">
                  <Col xs={5}></Col>
                  <Col xs={3} className="text-end text-muted">
                    {
                      subscriptionUpgradePreview.estimate.currentPlan
                        .display
                    }
                  </Col>
                  <Col xs={4} className="text-end text-success">
                    {
                      subscriptionUpgradePreview.estimate.proposedPlan
                        .display
                    }
                  </Col>
                </Row>
                <Row className="g-2 mb-1">
                  <Col xs={5}>Quantity</Col>
                  <Col xs={3} className="text-end">
                    {subscriptionUpgradePreview.estimate.currentPlan.quantity}
                  </Col>
                  <Col xs={4} className="text-end fw-bold">
                    {subscriptionUpgradePreview.estimate.proposedPlan.quantity}
                  </Col>
                </Row>
                <Row className="g-2 mb-1">
                  <Col xs={5}>Price / server</Col>
                  <Col xs={3} className="text-end text-nowrap">
                    {
                      subscriptionUpgradePreview.estimate.currentPlan
                        .priceDisplay
                    }
                  </Col>
                  <Col xs={4} className="text-end text-nowrap fw-bold">
                    {
                      subscriptionUpgradePreview.estimate.proposedPlan
                        .priceDisplay
                    }
                  </Col>
                </Row>
                <Row className="g-2 mb-1">
                  <Col xs={5}>Discounts</Col>
                  <Col xs={3} className="text-end text-success text-nowrap">
                    -
                    {
                      subscriptionUpgradePreview.estimate.currentPlan
                        .discountDisplay
                    }
                  </Col>
                  <Col xs={4} className="text-end text-success text-nowrap">
                    -
                    {
                      subscriptionUpgradePreview.estimate.proposedPlan
                        .discountDisplay
                    }
                  </Col>
                </Row>
                <Row className="g-2 mb-1">
                  <Col xs={5}>Subtotal</Col>
                  <Col xs={3} className="text-end text-nowrap">
                    {
                      subscriptionUpgradePreview.estimate.currentPlan
                        .subscriptionSubtotalDisplay
                    }
                  </Col>
                  <Col xs={4} className="text-end text-nowrap">
                    {
                      subscriptionUpgradePreview.estimate.proposedPlan
                        .subscriptionSubtotalDisplay
                    }
                  </Col>
                </Row>
                <Row className="g-2 mb-1">
                  <Col xs={5}>
                    VAT (
                    {
                      subscriptionUpgradePreview.estimate.proposedPlan
                        .taxPercentDisplay
                    }
                    )
                  </Col>
                  <Col xs={3} className="text-end text-nowrap">
                    {
                      subscriptionUpgradePreview.estimate.currentPlan
                        .subscriptionTaxDisplay
                    }
                  </Col>
                  <Col xs={4} className="text-end text-nowrap">
                    {
                      subscriptionUpgradePreview.estimate.proposedPlan
                        .subscriptionTaxDisplay
                    }
                  </Col>
                </Row>
                <Row className="g-2 border-top pt-2 mt-2 fw-bold fs-6">
                  <Col xs={5}>Recurring total</Col>
                  <Col xs={3} className="text-end text-nowrap">
                    {
                      subscriptionUpgradePreview.estimate.currentPlan
                        .subscriptionTotalDisplay
                    }
                  </Col>
                  <Col xs={4} className="text-end text-success text-nowrap">
                    {
                      subscriptionUpgradePreview.estimate.proposedPlan
                        .subscriptionTotalDisplay
                    }
                  </Col>
                </Row>
              </div>

              <div className="border rounded p-3 mb-3 fs-7">
                <div className="fw-bold mb-2">Proration</div>
                <div className="d-flex justify-content-between mb-1">
                  <span>New plan prorated charge</span>
                  <span>
                    {
                      subscriptionUpgradePreview.estimate.proposedPlan
                        .proratedItemChargeDisplay
                    }
                  </span>
                </div>
                <div className="d-flex justify-content-between mb-1 text-success">
                  <span>Unused plan credit</span>
                  <span>
                    -
                    {
                      subscriptionUpgradePreview.estimate.proposedPlan
                        .proratedItemCreditDisplay
                    }
                  </span>
                </div>
                <div className="d-flex justify-content-between mb-1">
                  <span>Prorated subtotal</span>
                  <span>
                    {
                      subscriptionUpgradePreview.estimate.amountDue
                        .prorationSubtotalDisplay
                    }
                  </span>
                </div>
                <div className="d-flex justify-content-between mb-1">
                  <span>Prorated tax</span>
                  <span>
                    {
                      subscriptionUpgradePreview.estimate.amountDue
                        .prorationTaxDisplay
                    }
                  </span>
                </div>
                <div className="d-flex justify-content-between border-top pt-2 mt-2 fs-6">
                  <b>
                    {subscriptionUpgradePreview.estimate.amountDue
                      .totalAmountDue < 0
                      ? "Credit applied now"
                      : "Amount due now"}
                  </b>
                  <b className="text-success">
                    {
                      subscriptionUpgradePreview.estimate.amountDue
                        .totalAmountDueDisplay
                    }
                  </b>
                </div>
              </div>

              <div className="d-flex justify-content-between gap-3 bg-light rounded p-3 fs-7">
                <div>
                  <div className="text-muted fs-7">Next charge date</div>
                  <b>
                    {
                      subscriptionUpgradePreview.estimate.amountDue
                        .nextChargeDateDisplay
                    }
                  </b>
                </div>
                <div className="text-end">
                  <div className="text-muted fs-7">Next charge amount</div>
                  <b className="fs-6 text-success">
                    {
                      subscriptionUpgradePreview.estimate.amountDue
                        .nextChargeAmountDisplay
                    }
                  </b>
                </div>
              </div>
            </ModalBody>
            <ModalFooter className="py-2">
              <Button
                size="sm"
                color="secondary"
                onClick={() => setSubscriptionUpgradePreview(null)}
              >
                {container.localeManager.strings.cancel}
              </Button>
              <Button
                size="sm"
                color="success"
                onClick={() =>
                  upgradeSubscription(
                    subscriptionUpgradePreview.name,
                    subscriptionUpgradePreview.quantity,
                  )
                }
              >
                <FontAwesomeIcon icon="arrow-up" />
                Confirm upgrade
              </Button>
            </ModalFooter>
          </>
        )}
      </Modal>
      <Modal
        isOpen={showBillingDetailModal}
        toggle={() => toggleBillingDetailModal(false)}
      >
        <ModalHeader
          toggle={() => toggleBillingDetailModal(false)}
          close={
            <Button
              color="active-light-primary"
              className="btn-icon btn-sm w-30px h-30px ms-auto"
              onClick={() => toggleBillingDetailModal(false)}
            >
              <FontAwesomeIcon icon="times"></FontAwesomeIcon>
            </Button>
          }
        >
          {container.localeManager.strings.billingInformation}
        </ModalHeader>
        <ModalBody>
          <div className="notice bg-light-success rounded border-success border border-dashed p-6 mb-3 d-flex justify-content-between align-items-center">
            <FontAwesomeIcon icon="info-circle"></FontAwesomeIcon>
            {container.localeManager.strings.billingDataHelpText}
          </div>
          <FormGroup row>
            <Col sm={6}>
              <Label>{container.localeManager.strings.firstName}</Label>
              <Input
                value={billingData.firstName}
                onChange={(e) =>
                  setBillingData({ ...billingData, firstName: e.target.value })
                }
              ></Input>
            </Col>
            <Col sm={6}>
              <Label>{container.localeManager.strings.lastName}</Label>
              <Input
                value={billingData.lastName}
                onChange={(e) =>
                  setBillingData({ ...billingData, lastName: e.target.value })
                }
              ></Input>
            </Col>
            <div className="mt-5">
              <Button
                disabled={
                  billingData.firstName == "" && billingData.lastName == ""
                }
                block
                color="success"
                onClick={createAccount}
              >
                {container.localeManager.strings.continueToPayment}
                <FontAwesomeIcon
                  icon="chevron-right"
                  className="ms-3 me-0"
                ></FontAwesomeIcon>
              </Button>
            </div>
          </FormGroup>
        </ModalBody>
      </Modal>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </LoadingOverlay>
  );
};

export default MySubscriptions;
